import { useEffect, useState } from "react";
import { GripVertical, ArrowUpDown } from "lucide-react";
import {
  DndContext, closestCenter, PointerSensor, useSensor, useSensors,
} from "@dnd-kit/core";
import {
  SortableContext, verticalListSortingStrategy, useSortable, arrayMove,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { getSiteSettings, updateSiteSettings } from "@/admin/adminApi";
import { CATEGORY_ORDER_LABELS, DEFAULT_CATEGORY_ORDER } from "@/admin/categoryConfig";

const SortableRow = ({ slug }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: slug });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.5 : 1 };

  return (
    <div
      ref={setNodeRef}
      style={style}
      data-testid={`admin-category-order-row-${slug}`}
      className="flex items-center gap-4 border-b border-[var(--border-default)] py-3 bg-[var(--bg-secondary)]"
    >
      <button {...attributes} {...listeners} data-testid={`admin-category-order-drag-${slug}`} className="cursor-grab text-white/40 hover:text-white shrink-0 px-2">
        <GripVertical size={18} />
      </button>
      <p className="text-white font-medium flex-1">{CATEGORY_ORDER_LABELS[slug] || slug}</p>
    </div>
  );
};

export default function AdminCategoryOrderPage() {
  const [order, setOrder] = useState(DEFAULT_CATEGORY_ORDER);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  useEffect(() => {
    getSiteSettings().then((data) => {
      const stored = data.categoryOrder && data.categoryOrder.length ? data.categoryOrder : DEFAULT_CATEGORY_ORDER;
      // A category added after the order was last saved won't be in the
      // stored list (e.g. this happened to Hard Lids) — append any such
      // missing slugs instead of silently dropping them from this page.
      const missing = DEFAULT_CATEGORY_ORDER.filter((slug) => !stored.includes(slug));
      setOrder([...stored, ...missing]);
      setLoading(false);
    });
  }, []);

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = order.indexOf(active.id);
    const newIndex = order.indexOf(over.id);
    setOrder(arrayMove(order, oldIndex, newIndex));
    setSaved(false);
  };

  const handleSave = async () => {
    setSaving(true);
    await updateSiteSettings(order);
    setSaving(false);
    setSaved(true);
  };

  if (loading) return <p className="text-white/60 font-mono text-sm">Loading…</p>;

  return (
    <div data-testid="admin-category-order-page">
      <div className="flex items-center gap-2 mb-2">
        <ArrowUpDown size={20} className="text-[var(--brand-primary)]" />
        <h1 className="font-display text-3xl text-white">Category Order</h1>
      </div>
      <p className="text-white/50 text-sm font-mono mb-6">
        Drag to reorder how categories appear on the Home page tile grid and in the Shop / Bushtech nav dropdowns.
        Some categories (12V Fridges &amp; LED Lighting) render as two tiles/links but move together as one group.
      </p>
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={order} strategy={verticalListSortingStrategy}>
          <div className="border-t border-[var(--border-default)] max-w-xl">
            {order.map((slug) => <SortableRow key={slug} slug={slug} />)}
          </div>
        </SortableContext>
      </DndContext>
      <div className="flex items-center gap-3 mt-6">
        <button data-testid="admin-category-order-save-button" onClick={handleSave} disabled={saving} className="btn-primary disabled:opacity-50">
          {saving ? "Saving…" : "Save Order"}
        </button>
        {saved && <span data-testid="admin-category-order-saved-message" className="text-emerald-400 text-sm font-mono">Saved — live on the storefront now.</span>}
      </div>
    </div>
  );
}
